import React, { useState } from "react";
import "@smile_identity/smart-camera-web";
import { BsArrowLeft } from "react-icons/bs";
import useLimits from "../../../hooks/useLimits";
import SmartCameraComponent from "../../../components/SmartCamera";
import { validateBvn } from "../../../utils/validations";
import useAuthAxios from "../../../utils/baseAxios";
import { API } from "../../../constants/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { errorMessage } from "../../../utils/errorMessage";
import ClipLoader from "react-spinners/ClipLoader";
import { livenessIcon } from "../../../assets/images";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";

const Level1Flow = ({
  setOpenLevel1Flow,
  setOpenKyc,
  setOpenKycOnboarding,
}: {
  setOpenLevel1Flow: (open: boolean) => void;
  setOpenKyc?: (open: boolean) => void;
  setOpenKycOnboarding?: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const axiosInstance = useAuthAxios();
  const { limits } = useLimits();
  const LEVEL_1 = limits[0];

  const [screen, setScreen] = useState<"unlock" | "liveness">("unlock");
  const [bvn, setBvn] = useState("");
  const [partnerParams, setPartnerParams] = useState("");
  const [images, setImages] = useState<
    { image_type_id: number; image: string }[]
  >([]);
  const [isImageCaptured, setIsImageCaptured] = useState(false);
  const [submit, setSubmit] = useState(0);

  const handlePostKyc = async ({
    bvn: bvnParam,
    partner_params,
    images: imagesParam,
  }: any) => {
    const response = await axiosInstance.post(API.verifyKyc, {
      bvn: bvnParam,
      partner_params,
      images: imagesParam,
    });
    return response.data;
  };

  const completeKyc = useMutation({
    mutationFn: handlePostKyc,
    onSuccess: (r: { message?: string }) => {
      toast.success(r?.message ?? "Verification successful");
      queryClient.invalidateQueries({ queryKey: ["get-kyc-details"] });
      setTimeout(() => {
        setOpenLevel1Flow(false);
        setOpenKycOnboarding?.(true);
      }, 1500);
    },
    onError: (error: any) => {
      toast.error(
        errorMessage((error?.data as any)?.message ?? String(error?.data))
      );
    },
  });

  const handleProceedToLiveness = () => {
    if (!validateBvn(bvn)) return;
    setScreen("liveness");
  };

  const handleLetsStart = () => {
    setIsImageCaptured(true);
  };

  const handleSubmit = () => {
    completeKyc.mutate({
      bvn,
      partner_params: { libraryVersion: partnerParams },
      images,
    });
  };

  if (isImageCaptured) {
    return (
      <div className="fixed inset-0 flex font-manrope justify-center items-start pt-16 pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm z-[100]">
        <SmartCameraComponent
          setIsImageCaptured={setIsImageCaptured}
          setSubmit={setSubmit}
          setPartnerParams={setPartnerParams}
          setImages={setImages}
        />
      </div>
    );
  }

  if (screen === "liveness") {
    return (
      <div className="fixed inset-0 flex font-manrope justify-center items-start pt-16 pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
        <div
          className={`w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white rounded-xl mx-auto dark:bg-[#192025] lgss:w-2/5 xxl:w-1/3 flex flex-col max-h-[85vh] overflow-hidden`}
        >
          <div className="shrink-0 p-6 pb-0">
            <div className="w-full flex justify-start gap-6 items-center">
              <button
                onClick={() => setScreen("unlock")}
                className="flex items-center justify-center"
              >
                <BsArrowLeft className="text-black dark:text-white text-[20px]" />
              </button>
              <h4 className="text-gray-900 dark:text-gray-100 font-sora font-medium text-[20px]">
                Face Recognition
              </h4>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-3">
              Your photos will only be used for identity verification and we
              will keep it strictly confidential.
            </p>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 p-6">
            {submit === 1 ? (
              <>
                <div className="mt-6 rounded-xl border border-gray-200 bg-[#E8F3FC] dark:bg-[#1C2227] dark:border-gray-600 p-4">
                  <p className="text-gray-800 dark:text-gray-200 text-[14px] font-medium text-center">
                    Your liveness image has been taken. Click the submit button
                    below to submit and verify.
                  </p>
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={completeKyc.isPending}
                  className="mt-8 w-full h-[52px] rounded-xl bg-secondary_button text-white font-semibold text-[15px] disabled:opacity-50 flex items-center justify-center"
                >
                  {completeKyc.isPending ? (
                    <ClipLoader color="#FFFFFF" size={28} />
                  ) : (
                    "Submit"
                  )}
                </button>
              </>
            ) : (
              <>
                <div className="mt-6 flex justify-center">
                  <img
                    src={livenessIcon}
                    alt="liveness"
                    className="w-[72px] h-[72px]"
                  />
                </div>
                <div className="mt-6 space-y-3">
                  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227] flex gap-3">
                    <IoIosCheckmarkCircleOutline className="text-gray-500 dark:text-gray-400 shrink-0 mt-0.5" />
                    <div>
                      <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                        There must be good lighting
                      </h5>
                      <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-1">
                        Ensure that the face is evenly illuminated and avoid
                        backlight or strong side light.
                      </p>
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227] flex gap-3">
                    <IoIosCheckmarkCircleOutline className="text-gray-500 dark:text-gray-400 shrink-0 mt-0.5" />
                    <div>
                      <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                        Your face must be inside the frame
                      </h5>
                      <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-1">
                        Face the camera directly, with your head centered and at
                        an appropriate distance.
                      </p>
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227] flex gap-3">
                    <IoIosCheckmarkCircleOutline className="text-gray-500 dark:text-gray-400 shrink-0 mt-0.5" />
                    <div>
                      <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                        You are not wearing glasses
                      </h5>
                      <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-1">
                        Remove glasses, hats and other items to reveal your
                        complete facial features.
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleLetsStart}
                  className="mt-8 w-full h-[52px] rounded-xl bg-secondary_button text-white font-semibold text-[15px]"
                >
                  Let&apos;s Start
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex font-manrope justify-center items-start pt-16 pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
      <div
        className={`w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white rounded-xl mx-auto dark:bg-[#192025] lgss:w-2/5 xxl:w-1/3 flex flex-col max-h-[85vh] overflow-hidden`}
      >
        <div className="shrink-0 p-6 pb-0">
          <div className="w-full flex justify-start gap-6 items-center">
            <button
              onClick={() => {
                setOpenLevel1Flow(false);
                setOpenKycOnboarding?.(true);
              }}
              className="flex items-center justify-center"
            >
              <BsArrowLeft className="text-black dark:text-white text-[20px]" />
            </button>
            <h4 className="text-gray-900 dark:text-gray-100 font-sora font-medium text-[20px]">
              Level 1 Verification
            </h4>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0 p-6 pt-4">
          <h5 className="text-gray-900 dark:text-gray-100 font-sora font-bold text-[20px] mt-2">
            What you'll unlock
          </h5>
          <div className="mt-3 rounded-xl border border-gray-200 bg-[#E8F3FC] dark:bg-[#1C2227] dark:border-gray-600 p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[15px]">
                Level 1
              </h5>
              <span className="px-2 py-0.5 bg-[#D4EBFD] dark:bg-secondary_button/30 text-secondary_button text-[10px] font-bold uppercase rounded">
                CURRENT LEVEL
              </span>
            </div>
            <div className="mt-3 space-y-4 bg-white dark:bg-[#1C2227] p-4 rounded-xl">
              <div className="flex justify-between border-b border-gray-200 dark:border-gray-600 pb-2 text-[13px]">
                <span className="text-gray-900 dark:text-gray-100 font-medium text-[14px]">
                  Daily Trading Limit
                </span>
                <span className="text-gray-900 dark:text-gray-100 font-bold text-[20px]">
                  <span className="text-[14px]">$</span>
                  {LEVEL_1.tradingRaw.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-gray-900 dark:text-gray-100 font-medium text-[14px]">
                  Daily Wallet Withdrawal
                </span>
                <span className="text-gray-900 dark:text-gray-100 font-bold text-[20px]">
                  <span className="text-[14px]">$</span>
                  {LEVEL_1.withdrawalRaw.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <h5 className="text-gray-900 dark:text-gray-100 font-sora font-bold text-[20px] mt-6">
            Requirements
          </h5>
          <div className="mt-3 space-y-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
              <div className="flex items-center gap-2 flex-wrap">
                <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                  BVN Verification
                </h5>
                <span className="px-2 py-0.5 bg-[#FDEDDB] dark:bg-[#F28A0F]/20 text-[#F28A0F] dark:text-[#E8A84A] text-[9px] font-bold uppercase rounded">
                  REQUIRED
                </span>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-1">
                Enter your Bank Verification Number
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
              <div className="flex items-center gap-2 flex-wrap">
                <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                  Liveness Check
                </h5>
                <span className="px-2 py-0.5 bg-[#FDEDDB] dark:bg-[#F28A0F]/20 text-[#F28A0F] dark:text-[#E8A84A] text-[9px] font-bold uppercase rounded">
                  REQUIRED
                </span>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-1">
                Take a selfie and follow motion prompts
              </p>
            </div>
          </div>

          <div className="mt-6">
            <label className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
              BVN
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={11}
              value={bvn}
              onChange={(e) => setBvn(e.target.value.replace(/\D/g, ""))}
              placeholder="Type here"
              className="w-full h-[52px] mt-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[14px] outline-none"
            />
            <p className="text-gray-500 dark:text-gray-400 text-[12px] mt-1">
              You can dial the shortcode (*565*0#) to see your BVN.
            </p>
          </div>

          <button
            onClick={handleProceedToLiveness}
            disabled={!bvn}
            className="mt-8 w-full h-[52px] rounded-xl bg-secondary_button text-white font-semibold text-[15px] disabled:opacity-50"
          >
            Proceed to liveness check
          </button>
        </div>
      </div>
    </div>
  );
};

export default Level1Flow;
